import { useMemo, useState } from 'react';
import type { CalendarEventDto } from '../../api/calendar';
import { Avatar } from '../../components/shared/Avatar';
import { tCalendar } from '../../lib/calendar-i18n';

/**
 * 상세 창 오른쪽 칸의 참석자 명단 (2026-10-19 사용자가 시안 D' 로 고름).
 *
 * - 순서: 나 → 작성자 → 상태(참석 → 미정(PENDING) → 미정(TENTATIVE) → 불참) → 이름순.
 * - 상태 칩: 「전체 n · 참석 n · 미정 n · 불참 n」 — 눌러 그 사람만 본다. 0명인 상태는 칩이 없고,
 *   상태가 하나뿐이면 칩 줄 자체를 그리지 않는다(거를 것이 없다).
 *   ⚠ PENDING·TENTATIVE 는 ko 에서 둘 다 「미정」이다(2026-09-29 사용자 결정) — 칩은 «보이는 글자»로 묶어
 *     같은 글자 칩이 둘 생기지 않게 한다. 글자가 다른 언어(en: Pending / Maybe)에서는 따로 나온다.
 * - 이름 찾기 칸: 20명이 «넘을 때만».
 * - 목록은 칸 안에서만 스크롤한다(높이는 CSS — 왼쪽 정보 높이에 맞춘다).
 */
type Attendee = CalendarEventDto['attendees'][number];

const STATUS_ORDER = ['ACCEPTED', 'PENDING', 'TENTATIVE', 'DECLINED'] as const;
const ATTENDEE_SEARCH_MIN = 21;

function statusOf(a: Attendee): string {
  return (a.status ?? 'PENDING').toUpperCase();
}
function statusLabel(status: string): string {
  return tCalendar(`calendar.rsvp.${status.toLowerCase()}` as never);
}

interface Props {
  attendees: ReadonlyArray<Attendee>;
  creatorId: string;
  myUserId?: string | null;
  nameOf: (userId: string) => string;
}

export function EventDetailAttendeePanel({ attendees, creatorId, myUserId, nameOf }: Props) {
  const [filter, setFilter] = useState<string | null>(null);  // 칩 글자(null = 전체)
  const [query, setQuery] = useState('');

  const sorted = useMemo(() => {
    const pin = (a: Attendee) => (a.userId === myUserId ? 0 : a.userId === creatorId ? 1 : 2);
    const rank = (a: Attendee) => {
      const i = (STATUS_ORDER as readonly string[]).indexOf(statusOf(a));
      return i < 0 ? STATUS_ORDER.length : i;
    };
    return attendees
      .map((a) => ({ a, name: nameOf(a.userId), label: statusLabel(statusOf(a)) }))
      .sort((x, y) => pin(x.a) - pin(y.a) || rank(x.a) - rank(y.a) || x.name.localeCompare(y.name, 'ko'));
  }, [attendees, creatorId, myUserId, nameOf]);

  // 칩 — 보이는 글자로 묶는다(순서는 STATUS_ORDER 를 따른다).
  const groups = useMemo(() => {
    const out: Array<{ label: string; n: number }> = [];
    for (const row of sorted) {
      const g = out.find((x) => x.label === row.label);
      if (g) g.n += 1; else out.push({ label: row.label, n: 1 });
    }
    const order = (label: string) => STATUS_ORDER.findIndex((s) => statusLabel(s) === label);
    return out.sort((x, y) => order(x.label) - order(y.label));
  }, [sorted]);

  const activeFilter = filter && groups.some((g) => g.label === filter) ? filter : null;
  const showSearch = attendees.length >= ATTENDEE_SEARCH_MIN;
  const q = showSearch ? query.trim().toLowerCase() : '';
  const shown = sorted.filter((r) => (!activeFilter || r.label === activeFilter) && (!q || r.name.toLowerCase().includes(q)));

  return (
    <div className="calendar-detail-people">
      <div className="calendar-detail-people-head">
        {tCalendar('calendar.detail.attendee_count', { n: attendees.length })}
        {shown.length !== attendees.length && (
          <span className="calendar-detail-people-showing">
            {' · '}{tCalendar('calendar.detail.attendee_showing', { n: shown.length })}
          </span>
        )}
      </div>

      {groups.length > 1 && (
        <div className="calendar-detail-people-chips" role="group" aria-label={tCalendar('calendar.detail.attendee_filter')}>
          <button
            type="button"
            className={`calendar-detail-people-chip${activeFilter === null ? ' calendar-detail-people-chip--on' : ''}`}
            aria-pressed={activeFilter === null}
            onClick={() => setFilter(null)}
          >
            {tCalendar('calendar.detail.attendee_filter_all')} {attendees.length}
          </button>
          {groups.map((g) => (
            <button
              key={g.label}
              type="button"
              className={`calendar-detail-people-chip${activeFilter === g.label ? ' calendar-detail-people-chip--on' : ''}`}
              aria-pressed={activeFilter === g.label}
              onClick={() => setFilter(g.label)}
            >
              {g.label} {g.n}
            </button>
          ))}
        </div>
      )}

      {showSearch && (
        <input
          type="search"
          className="calendar-detail-people-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={tCalendar('calendar.detail.attendee_search')}
          aria-label={tCalendar('calendar.detail.attendee_search')}
        />
      )}

      <ul className="calendar-detail-people-list">
        {shown.map(({ a, name, label }) => (
          <li key={a.userId} className="calendar-detail-attendee calendar-detail-attendee--row">
            <span aria-hidden><Avatar userId={Number(a.userId)} name={name} size="xs" /></span>
            <span className="calendar-detail-people-name">
              {name}
              {a.userId === creatorId && (
                <span className="calendar-detail-people-creator"> · {tCalendar('calendar.detail.attendee_creator')}</span>
              )}
            </span>
            <span className={`calendar-detail-attendee-status calendar-detail-attendee-status--${statusOf(a).toLowerCase()}`}>{label}</span>
          </li>
        ))}
        {shown.length === 0 && (
          <li className="calendar-detail-people-empty">{tCalendar('calendar.detail.attendee_search_empty')}</li>
        )}
      </ul>
    </div>
  );
}
